import { Component, OnInit, OnDestroy, ViewChild, ElementRef, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors } from '@angular/forms';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Placeholder from '@tiptap/extension-placeholder';

@Component({
    selector: 'app-tiptap-editor',
    templateUrl: './tiptap.component.html',
    styleUrls: ['./tiptap-material.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TiptapComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => TiptapComponent),
            multi: true
        }
    ]
})
export class TiptapComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
    @ViewChild('editorElement', { static: true }) editorElement!: ElementRef;

    @Input() placeholder: string = 'Enter text...';
    @Input() label: string = '';
    @Input() required: boolean = false;

    editor: Editor | null = null;
    isFocused: boolean = false;
    isDisabled: boolean = false;

    // ControlValueAccessor callbacks
    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    ngOnInit(): void {
        this.editor = new Editor({
            element: this.editorElement.nativeElement,
            extensions: [
                StarterKit,
                Underline,
                Subscript,
                Superscript,
                Placeholder.configure({
                    placeholder: this.placeholder
                })
            ],
            editorProps: {
                attributes: {
                    class: 'tiptap-editor-prose'
                }
            },
            onUpdate: ({ editor }) => {
                const html = editor.getHTML();
                this.onChange(html);
            },
            onFocus: () => {
                this.isFocused = true;
            },
            onBlur: () => {
                this.isFocused = false;
                this.onTouched();
            }
        });
    }

    ngOnDestroy(): void {
        this.editor?.destroy();
    }

    // ControlValueAccessor Implementation
    writeValue(value: string): void {
        if (this.editor && value !== this.editor.getHTML()) {
            this.editor.commands.setContent(value || '');
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        this.editor?.setEditable(!isDisabled);
    }

    // Validator Implementation
    validate(control: AbstractControl): ValidationErrors | null {
        if (this.required && !control.value) {
            return { required: true };
        }
        return null;
    }

    // Toolbar Actions
    toggleBold(): void {
        this.editor?.chain().focus().toggleBold().run();
    }

    toggleItalic(): void {
        this.editor?.chain().focus().toggleItalic().run();
    }

    toggleUnderline(): void {
        this.editor?.chain().focus().toggleUnderline().run();
    }

    toggleStrike(): void {
        this.editor?.chain().focus().toggleStrike().run();
    }

    toggleSubscript(): void {
        this.editor?.chain().focus().toggleSubscript().run();
    }

    toggleSuperscript(): void {
        this.editor?.chain().focus().toggleSuperscript().run();
    }

    setParagraph(): void {
        this.editor?.chain().focus().setParagraph().run();
    }

    toggleOrderedList(): void {
        this.editor?.chain().focus().toggleOrderedList().run();
    }

    toggleBulletList(): void {
        this.editor?.chain().focus().toggleBulletList().run();
    }

    // Check if editor has content (for label floating)
    hasContent(): boolean {
        if (!this.editor) return false;
        const text = this.editor.getText().trim();
        return text.length > 0;
    }
}
